import type { ReactElement } from 'react';
import React from 'react';
import { Layout, Menu } from 'antd';
import type { MenuProps } from 'antd';
import Icon from '@ant-design/icons';

import {
  AreaChartOutlined,
  PhoneOutlined,
  PrinterOutlined,
  DashboardOutlined,
  SettingOutlined,
  UserOutlined,
  BarChartOutlined,
  LineChartOutlined,
  ExperimentOutlined,
  ToolOutlined,
  ApiOutlined,
  CompassOutlined,
  PieChartOutlined,
  LinkOutlined,
  SolutionOutlined,
  DownloadOutlined,
  AuditOutlined,
  ScanOutlined,
  RadarChartOutlined,
} from '@ant-design/icons';
import type { MenuTheme } from 'antd/lib/menu/MenuContext';
import { useStoreState, useStoreActions } from '../../store';
import { Link } from 'react-router-dom';
import type { NavigationLink } from '../../store/navigation/types';
import { createUseStyles } from 'react-jss';
import { useColumnWidth } from '../../hooks/useColumnWidth';
import logo from '~/assets/symbol.png';
import { ReactComponent as LogoLight } from '~/assets/logo-light.svg';
import { ReactComponent as Delivery } from '~/assets/delivery-truck.svg';
import { ShipiumTag } from '../../components/shared/ShipiumTag';
import { shipiumApplicationUrlHelper } from '../../utils/shipiumApplicationUrlHelper';
import { getShipiumDomain } from '~/utils/shipiumDomain';
import { useNavigation } from '~/hooks/useNavigation';
import { useSession } from '~/hooks/useSession';
const { Sider } = Layout;

type MenuItem = NonNullable<MenuProps['items']>[number];

export interface BaseSiderProps {
  theme?: MenuTheme;
}

const v3tov4IconMap: Record<string, ReactElement> = {
  dashboard: <DashboardOutlined />,
  user: <UserOutlined />,
  setting: <SettingOutlined />,
  'bar-chart': <BarChartOutlined />,
  'area-chart': <AreaChartOutlined />,
  'line-chart': <LineChartOutlined />,
  experiment: <ExperimentOutlined />,
  'tool-outlined': <ToolOutlined />,
  'solution-outlined': <SolutionOutlined />,
  compass: <CompassOutlined />,
  'pie-chart': <PieChartOutlined />,
  LinkOutlined: <LinkOutlined />,
  printer: <PrinterOutlined />,
  download: <DownloadOutlined />,
  truck: <Icon style={{ fontSize: 20, marginLeft: 0, marginRight: 0 }} component={Delivery} />,
};

const useStyles = createUseStyles({
  logoAvatar: {
    backgroundColor: '#ff8c00',
  },
  logo: {
    position: 'relative',
    overflow: 'hidden',
    background: '#001529',
    cursor: 'pointer',
    transition: 'all .3s',
    '& > a': {
      display: 'flex',
      alignItems: 'center',
      height: '64px',
    },
  },
  logoTitle: {
    display: 'inline-block',
    margin: '0 0 0 10px',
    color: '#fff',
    fontWeight: 600,
    fontSize: '22px',
    verticalAlign: 'middle',
    animation: 'fade-in',
    animationDuration: '.3s',
  },
  hidden: {
    display: 'none',
  },
  version: {
    marginTop: '-18px',
    right: '30px',
    color: '#fff',
    fontSize: '12px',
    position: 'absolute',
  },
  versionCollapsed: {
    marginTop: '-8px',
    left: 0,
    right: 0,
    textAlign: 'center',
    color: '#fff',
    fontSize: '12px',
    position: 'absolute',
  },
  subMenu: {
    '& .ant-menu-submenu-title': {
      paddingLeft: '10px',
    },
  },
  navText: {
    marginLeft: '16px !important',
  },
  iconHolder: {
    '& :nth-child(1)': {
      marginLeft: '3px',
      marginRight: '3px',
    },
  },
  siderContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'auto',
    justifyContent: 'space-between',
  },
  siderLayout: {
    height: 'calc(100vh - 48px)',
    display: 'flex',
    flexDirection: 'column',
  },
});

export const BaseSider = ({ theme = 'dark' }: BaseSiderProps) => {
  const path = useStoreState((state) => state.router.location.pathname);
  const navigation = useNavigation();
  const homePath = Array.isArray(navigation) && navigation.length ? navigation[0].path : '';
  const classes = useStyles();

  const toggleNavigation = useStoreActions((actions) => actions.navigation.toggleNavigation);

  const isNavigationCollapsed = useStoreState((state) => state.navigation.isNavigationCollapsed);
  const isNavigationHidden = useStoreState((state) => state.navigation.isNavigationHidden);
  const { sessionPartnerUrl, isOptInEnabled } = useSession();

  const siderWidth = isNavigationHidden ? '100%' : '225px';
  const menuWidth = isNavigationCollapsed ? '80px' : '225px';
  const isSiderHidden = isNavigationCollapsed && isNavigationHidden;
  const isEuDomain = getShipiumDomain(window.location.hostname).isEu;
  const billingManagementOptinEnabled = isOptInEnabled('billing-management-opt-in') && !isEuDomain;
  const sonarConsoleOptinEnabled = isOptInEnabled('sonar-console-opt-in') && !isEuDomain;
  const packShipOptinEnabled = isOptInEnabled('pack-ship-opt-in') && !isEuDomain;

  useColumnWidth();

  const buildMenuItems = (links: NavigationLink[]): MenuItem[] => {
    return links.map((item): MenuItem => {
      const children = (item as { links?: NavigationLink[] }).links;
      if (Array.isArray(children)) {
        return {
          key: item.path,
          className: classes.subMenu,
          label: (
            <span className={classes.iconHolder}>
              {item.icon && v3tov4IconMap[item.icon]}
              <span>{item.name}</span>
            </span>
          ),
          children: buildMenuItems(children),
        };
      }
      if (item.external) {
        return {
          key: item.path,
          label: (
            <span>
              <a style={{ color: 'inherit' }} rel="noopener noreferrer" target="_blank" href={item.path}>
                {item.name}&nbsp;&nbsp;
                <LinkOutlined />
              </a>
            </span>
          ),
        };
      }
      const absolutePath = item.path.startsWith('/') ? item.path : `/${sessionPartnerUrl}/${item.path}`;
      return {
        key: item.path,
        label: (
          <Link to={absolutePath}>
            {item.icon && v3tov4IconMap[item.icon]}
            <span className={item.icon ? classes.navText : undefined}>{item.name}</span>
            {item.tag && (
              <ShipiumTag color="Coral" style={{ marginLeft: 5 }}>
                {item.tag}
              </ShipiumTag>
            )}
          </Link>
        ),
      };
    });
  };

  const secondaryMenuItems: MenuItem[] = (
    [
      billingManagementOptinEnabled
        ? {
            key: 'billing-management-menu-item',
            label: (
              <a
                style={{ color: 'inherit' }}
                rel="noopener noreferrer"
                href={shipiumApplicationUrlHelper(window.location.hostname, 'billing-management')}
              >
                <AuditOutlined />
                {!isNavigationHidden && <span className={classes.navText}>Billing Management</span>}
              </a>
            ),
          }
        : null,
      sonarConsoleOptinEnabled
        ? {
            key: 'sonar-console-menu-item',
            label: (
              <a
                style={{ color: 'inherit' }}
                rel="noopener noreferrer"
                href={shipiumApplicationUrlHelper(window.location.hostname, 'sonar')}
              >
                <RadarChartOutlined />
                {!isNavigationHidden && <span className={classes.navText}>Sonar Console</span>}
              </a>
            ),
          }
        : null,
      packShipOptinEnabled
        ? {
            key: 'pack-ship-menu-item',
            label: (
              <a
                style={{ color: 'inherit' }}
                rel="noopener noreferrer"
                href={shipiumApplicationUrlHelper(window.location.hostname, 'pack')}
              >
                <ScanOutlined />
                {!isNavigationHidden && <span className={classes.navText}>Pack App</span>}
              </a>
            ),
          }
        : null,
      {
        key: 'contact-shipium-menu-item',
        label: (
          <a
            style={{ color: 'inherit' }}
            rel="noopener noreferrer"
            target="_blank"
            href={'https://docs.shipium.com/docs/contacting-shipium-support'}
          >
            <PhoneOutlined />
            {!isNavigationHidden && <span className={classes.navText}>Contact Shipium</span>}
          </a>
        ),
      },
      {
        key: `/${sessionPartnerUrl}/service-status`,
        label: (
          <Link to={`/${sessionPartnerUrl}/service-status`}>
            <ApiOutlined />
            {!isNavigationHidden && <span className={classes.navText}>Service Status</span>}
          </Link>
        ),
      },
      {
        key: 'api-docs-menu-item',
        label: (
          <a
            style={{ color: 'inherit' }}
            rel="noopener noreferrer"
            target="_blank"
            href={'https://docs.shipium.com/docs'}
          >
            <LinkOutlined />
            {!isNavigationHidden && <span className={classes.navText}>API Documentation</span>}
          </a>
        ),
      },
    ] as (MenuItem | null)[]
  ).filter((item): item is MenuItem => item !== null);

  return isSiderHidden ? null : (
    <Sider width={siderWidth} collapsible collapsed={isNavigationCollapsed} onCollapse={toggleNavigation}>
      <div className={classes.siderLayout}>
        <div className={classes.logo} style={{ padding: isNavigationCollapsed ? '4px 8px 8px' : '4px 16px 8px' }}>
          <Link to={homePath}>
            {isNavigationCollapsed && <img src={logo} alt="Shipium" style={{ width: 48, marginInline: 'auto' }} />}
            {!isNavigationCollapsed && <LogoLight style={{ width: 180 }} />}
          </Link>
          <span className={isNavigationCollapsed ? classes.versionCollapsed : classes.version}>
            v.{process.env.REACT_APP_VERSION}
          </span>
        </div>
        <div className={classes.siderContent}>
          <Menu theme={theme} style={{ width: menuWidth }} selectedKeys={[path]} items={buildMenuItems(navigation)} />
          <Menu theme={theme} style={{ width: menuWidth }} selectedKeys={[path]} items={secondaryMenuItems} />
        </div>
      </div>
    </Sider>
  );
};

export default BaseSider;
